import React, { useState, useEffect, useCallback } from "react";
import { get, post, patch, del } from "../../../helpers/api_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import {
  Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane,
  Button, Spinner, Row, Col,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const ODATA_BASE = "https://buharaapi.stechomeyazilim.info";

// Türkçe orijinal metin "About" tablosunda About_Html (Edm.Binary) olarak tutuluyor.
// Değer çift base64: OData binary katmanı + kolonda saklanan base64(HTML).
const decodeAboutHtml = (raw) => {
  if (!raw) return "";
  try { return decodeURIComponent(escape(atob(atob(raw)))); } catch (e) { /* devam */ }
  try { return decodeURIComponent(escape(atob(raw))); } catch (e) { /* devam */ }
  return raw;
};
const encodeAboutHtml = (html) => {
  const logical = btoa(unescape(encodeURIComponent(html || ""))); // base64(utf8 HTML)
  return btoa(logical); // OData binary katmanı
};

const LANGS = [
  { code: "tr", label: "🇹🇷 Türkçe" },
  { code: "en", label: "🇬🇧 English" },
  { code: "fr", label: "🇫🇷 Français" },
  { code: "de", label: "🇩🇪 Deutsch" },
  { code: "nl", label: "🇳🇱 Nederlands" },
];

export default function AboutTranslations() {
  document.title = "Hakkımızda Çevirileri | BUHARA - Admin Paneli";

  const [activeTab, setActiveTab] = useState("tr");
  const [records, setRecords]     = useState({});   // { en: {ID, About_Html}, ... }
  const [htmlMap, setHtmlMap]     = useState({});   // { en: "<p>...</p>", ... }
  const [trRecord, setTrRecord]   = useState(null); // About tablosundaki Türkçe kayıt
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      // Çeviriler (About_Lang)
      const res = await get(`${ODATA_BASE}/About_Lang`);
      const items = res?.value || [];
      const recMap = {};
      const hMap   = {};
      items.forEach(item => {
        recMap[item.Lang_Code] = item;
        hMap[item.Lang_Code]   = item.About_Html || "";
      });

      // Türkçe orijinal (About tablosu)
      try {
        const trRes = await get(`${ODATA_BASE}/About`);
        const trItem = (trRes?.value || [])[0] || null;
        setTrRecord(trItem);
        hMap["tr"] = trItem ? decodeAboutHtml(trItem.About_Html) : "";
      } catch (e) {
        console.error("About (tr) fetch error:", e);
      }

      setRecords(recMap);
      setHtmlMap(hMap);
    } catch (err) {
      console.error("About_Lang fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleSave = async (lang) => {
    setSaving(true);
    try {
      if (lang === "tr") {
        // Türkçe orijinali About tablosuna binary (çift base64) olarak yaz.
        const payload = { About_Html: encodeAboutHtml(htmlMap["tr"] || "") };
        if (trRecord?.ID) {
          await patch(`${ODATA_BASE}/About(${trRecord.ID})`, payload);
          toastr.success("Türkçe Hakkımızda metni güncellendi.");
        } else {
          await post(`${ODATA_BASE}/About`, payload);
          toastr.success("Türkçe Hakkımızda metni eklendi.");
        }
        await fetchRecords();
        return;
      }
      const existing = records[lang];
      const payload  = { Lang_Code: lang, About_Html: htmlMap[lang] || "" };
      if (existing?.ID) {
        await patch(`${ODATA_BASE}/About_Lang(${existing.ID})`, payload);
        toastr.success(`${lang.toUpperCase()} çevirisi güncellendi.`);
      } else {
        await post(`${ODATA_BASE}/About_Lang`, payload);
        toastr.success(`${lang.toUpperCase()} çevirisi eklendi.`);
      }
      await fetchRecords();
    } catch (err) {
      toastr.error("Kayıt sırasında hata oluştu.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lang) => {
    if (lang === "tr") return; // Türkçe orijinal silinemez
    const existing = records[lang];
    if (!existing?.ID) return;
    if (!window.confirm(`${lang.toUpperCase()} çevirisini silmek istiyor musunuz?`)) return;
    setSaving(true);
    try {
      await del(`${ODATA_BASE}/About_Lang(${existing.ID})`);
      toastr.success(`${lang.toUpperCase()} çevirisi silindi.`);
      await fetchRecords();
      setHtmlMap(prev => ({ ...prev, [lang]: "" }));
    } catch {
      toastr.error("Silme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Yönetim" breadcrumbItem="Hakkımızda Çevirileri" />
        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="warning" />
                    <span className="ms-2">Yükleniyor...</span>
                  </div>
                ) : (
                  <>
                    <Nav tabs className="mb-3">
                      {LANGS.map(l => (
                        <NavItem key={l.code}>
                          <NavLink
                            className={activeTab === l.code ? "active fw-bold" : ""}
                            onClick={() => setActiveTab(l.code)}
                            style={{ cursor: "pointer" }}
                          >
                            {l.label}
                            {(l.code === "tr" ? trRecord : records[l.code]) && (
                              <span className="ms-1 badge bg-success" style={{ fontSize: 9 }}>✓</span>
                            )}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>

                    <TabContent activeTab={activeTab}>
                      {LANGS.map(l => (
                        <TabPane tabId={l.code} key={l.code}>
                          <p className="text-muted mb-2">
                            {l.code === "tr"
                              ? "🇹🇷 Türkçe — Hakkımızda orijinal metni (web sitesindeki ana içerik)."
                              : `${l.label} — Hakkımızda HTML içeriği.`}
                            {(l.code === "tr" ? trRecord : records[l.code])
                              ? " (Mevcut kayıt güncelleniyor)"
                              : " (Yeni kayıt eklenecek)"}
                          </p>
                          <CKEditor
                            editor={ClassicEditor}
                            data={htmlMap[l.code] || ""}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setHtmlMap(prev => ({ ...prev, [l.code]: data }));
                            }}
                          />
                          <div className="d-flex gap-2 mt-3">
                            <Button
                              color="warning"
                              disabled={saving}
                              onClick={() => handleSave(l.code)}
                            >
                              {saving ? <Spinner size="sm" /> : null}
                              {" "}{(l.code === "tr" ? trRecord : records[l.code]) ? "Güncelle" : "Kaydet"}
                            </Button>
                            {l.code !== "tr" && records[l.code] && (
                              <Button
                                color="danger"
                                outline
                                disabled={saving}
                                onClick={() => handleDelete(l.code)}
                              >
                                Sil
                              </Button>
                            )}
                          </div>
                        </TabPane>
                      ))}
                    </TabContent>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
